import { effectSlot, isIncompleteTrigger } from './flow.js';
/**
 * The edition number of a run against the DRAFT rather than a published edition.
 *
 * Editions are numbered from 1, so 0 can never collide with a real one. Preview uses it: the
 * teacher is running what they are editing, which no audience has been served. It also makes the
 * one record that must never be mistaken for a student's self-identifying in an export.
 */
export const DRAFT_EDITION = 0;
export function startSession(experienceId, edition, id, now = new Date()) {
    return {
        id, experienceId, edition, startedAt: now.toISOString(),
        rolls: {}, effects: {}, attempts: [], visits: [],
    };
}
/** Append-only, and returns a new record rather than mutating -- the log is history. */
export function recordAttempt(session, attempt) {
    return { ...session, attempts: [...session.attempts, attempt] };
}
/**
 * Log an arrival, unless they are already here.
 *
 * The guard is what makes this safe to call from anywhere a navigation lands, including a route
 * that re-fires for a reason of its own: arriving at the task you are already on is not a visit.
 * `stay` never passes through here at all — it does not navigate — so the only thing this has to
 * refuse is a repeat of the same destination.
 *
 * `visits` may be absent on a session started before the field existed; reading it as empty is
 * the same forgiving read every other stored shape gets.
 */
export function recordVisit(session, taskId, now = new Date()) {
    const visits = session.visits ?? [];
    if (visits[visits.length - 1]?.taskId === taskId)
        return session;
    return { ...session, visits: [...visits, { taskId, at: now.toISOString() }] };
}
/**
 * Apply this visit's arrival policy: drop the named blocks' latched effects, once.
 *
 * `blockIds` is what the caller's policy says to RESET -- the blocks in this task whose effects
 * should not survive a return. The caller resolves that, because the policy is per block and the
 * session has never known which blocks a task holds.
 *
 * Returns the session unchanged, by identity, when this visit is already settled or there is
 * nothing to drop, so a caller can skip a re-render on the ordinary arrival.
 *
 * DROPS BY BLOCK, NOT BY SLOT PATTERN. Every effect carries its `blockId`, so "every effect
 * targeting a block in this task" is expressible without parsing the key it is filed under --
 * and it stays expressible when a new effect kind arrives with a differently shaped slot.
 *
 * KNOWN LIMIT: an effect records what it changed, never which task's Change set it, so this
 * cannot tell "my own last visit" from "another task deliberately turned this on for me". It is
 * acceptable while every effect is a loosening -- a bypass, a boolean flag, a sensor display
 * mode -- none of which creates or reveals content. It needs revisiting when effects grow teeth.
 */
export function settleArrival(session, taskId, blockIds) {
    const visits = session.visits ?? [];
    const last = visits.map((v, i) => [v, i]).filter(([v]) => v.taskId === taskId).pop();
    if (!last || last[0].settled)
        return session;
    const drop = new Set(blockIds);
    const effects = Object.fromEntries(Object.entries(session.effects).filter(([, e]) => !drop.has(e.blockId)));
    return {
        ...session,
        effects,
        visits: visits.map((v, i) => (i === last[1] ? { ...v, settled: true } : v)),
    };
}
/**
 * UNDO A TASK'S HISTORY: make it one the run has never reached.
 *
 * Not the same gesture as Reset, and the difference is the point. Reset clears the ANSWERS so the
 * student can try again, and the attempt history keeps climbing -- attempt 3 follows attempt 2,
 * which is what makes "attempts" mean anything. This removes the visits and the attempts
 * themselves, so the next arrival is a FIRST arrival: the arrival policy runs again and a
 * `visits` condition reads zero. It is the only way to re-test what a student meets on their way
 * IN, which no amount of resetting can reproduce.
 *
 * The answers go with the attempts because they live inside them (`AttemptRecord.answers`); there
 * is no version of this that keeps one and not the other.
 *
 * Latched effects go too, and `blockIds` is here for exactly that reason -- the same reason
 * `settleArrival` takes it. A task reporting "never visited" while still wearing scaffolding some
 * Change latched on its blocks is a state the student could never be in, and leaving it would
 * make the one button meant to produce a clean slate produce a contradictory one.
 *
 * `rolls` deliberately SURVIVE. Clearing is "run this again", not "run it as a different
 * student": the teacher is usually retrying the very numbers they were just looking at, and
 * Reroll already exists for the other intent.
 */
export function clearTask(session, taskId, blockIds) {
    const drop = new Set(blockIds);
    return {
        ...session,
        effects: Object.fromEntries(Object.entries(session.effects).filter(([, e]) => !drop.has(e.blockId))),
        attempts: session.attempts.filter((a) => a.taskId !== taskId),
        visits: (session.visits ?? []).filter((v) => v.taskId !== taskId),
    };
}
/**
 * The same, for every task at once: the run as it was the moment Preview opened.
 *
 * Every effect goes, with no `blockIds` to narrow by, because nothing is being kept -- this is
 * the state a fresh `start` produces. `rolls` survive here too, so "clear the run" and "reset"
 * agree about which student you are: `start` takes `seedRolls` for the same reason.
 */
export function clearRun(session) {
    return { ...session, effects: {}, attempts: [], visits: [] };
}
/** How many times they have arrived at this task, the CURRENT visit included. */
export function visitCount(session, taskId) {
    return (session.visits ?? []).filter((v) => v.taskId === taskId).length;
}
/**
 * Whether a visit to this task ever answered nothing.
 *
 * The one place the two readings of "visit" can legitimately differ. `visitCount` counts
 * arrivals; `visitsOf` returns what each arrival LEFT BEHIND, so an arrival that submitted
 * nothing -- a page of text, a task opened and backed out of -- is counted but has nothing to
 * show. Review flips through the second; a `visits` condition asks the first.
 *
 * Exported so the relationship is asserted rather than assumed: `visitsOf` can never return more
 * entries than there were arrivals, and returns fewer exactly when this is true.
 */
export function visitsWereSkipped(session, taskId) {
    return visitsOf(session, taskId).length < visitCount(session, taskId);
}
/**
 * The scope that holds the EXPERIENCE's variables, as opposed to any one task's.
 *
 * A reserved key in the same `rolls` map rather than a second mechanism, so experience variables
 * inherit the whole lifecycle a task's already have — drawn once, sticky for the run, fresh on
 * restart or Reroll, topped up when one is added — without a parallel implementation to keep in
 * step. It cannot collide with a real task id, which is always `tsk_`-prefixed.
 *
 * The point of the separate scope is that it is NOT per task: `river` has to be the same river in
 * task 1 and task 5, or a scenario that continues across tasks contradicts itself. Task variables
 * stay keyed by task, where re-rolling per task is the right behaviour.
 */
export const EXPERIENCE_ROLL_SCOPE = '__experience__';
/**
 * The roll for a task: every value drawn once and never redrawn, and every value drawn.
 *
 * A value already in the roll keeps what it had — that is the whole point, and it is what lets a
 * student routed to a remediation task and back meet the same question rather than a fresh one.
 *
 * But "already rolled" is a claim about a KEY, not about the task, and the task's set of keys
 * grows. An author previews, goes back, adds a variable or a block, and previews again: the roll
 * is sticky per task per run, so it came back without the new key, and a `{name}` with no value
 * cannot resolve — it renders as the chip itself. Reroll appeared to be the fix because it
 * discards the roll and draws a complete one. It was reported as "the third chip would not
 * resolve", the third being the one most recently added.
 *
 * So the gap is topped up rather than the roll being redrawn. `produce` runs on every call now,
 * and all but the missing keys are thrown away; it is a handful of random numbers, and the
 * alternative is asking every caller to declare its key set up front.
 *
 * The session is returned unchanged, by identity, when nothing was missing, so a caller can still
 * skip a re-render on the ordinary case.
 */
export function rollFor(session, taskId, produce) {
    const existing = session.rolls[taskId];
    const drawn = produce();
    if (!existing) {
        return { session: { ...session, rolls: { ...session.rolls, [taskId]: drawn } }, roll: drawn };
    }
    const added = Object.keys(drawn).filter(key => !(key in existing));
    if (added.length === 0)
        return { session, roll: existing };
    const roll = { ...existing, ...Object.fromEntries(added.map(key => [key, drawn[key]])) };
    return { session: { ...session, rolls: { ...session.rolls, [taskId]: roll } }, roll };
}
/**
 * Latch this submission's effects onto the session.
 *
 * Returns the session unchanged when nothing new applies, so a caller can compare by identity and
 * skip a re-render on the ordinary submission that changes no settings.
 */
export function latchEffects(session, effects) {
    if (effects.length === 0)
        return session;
    const next = { ...session.effects };
    for (const effect of effects)
        next[effectSlot(effect)] = effect;
    const changed = Object.keys(next).length !== Object.keys(session.effects).length
        || Object.entries(next).some(([slot, e]) => session.effects[slot] !== e);
    return changed ? { ...session, effects: next } : session;
}
const EMPTY = { all: 0, complete: 0, unique: 0, incorrect: 0 };
const firedIncomplete = (states) => states.some((s) => isIncompleteTrigger(s.trigger));
const firedIncorrect = (states) => states.some((s) => s.evaluation === 'incorrect');
/**
 * `complete` means the student ANSWERED, not that they answered correctly.
 *
 * It follows `isIncompleteTrigger` -- a blank -- and deliberately NOT `isRetryTrigger`, which also
 * covers sensor failure. flow.ts draws that line already and says why: *"a blank is the student
 * choosing not to answer yet, a bad fix is the hardware, and a teacher wants to respond to them
 * differently."* A student whose compass could not get a reading did everything asked of them, so
 * counting that against their completion would penalise them for the device.
 */
function tally(attempts, pick) {
    const picked = attempts.map(pick).filter((p) => p !== null);
    if (picked.length === 0)
        return EMPTY;
    return {
        all: picked.length,
        complete: picked.filter((p) => !firedIncomplete(p.states)).length,
        incorrect: picked.filter((p) => firedIncorrect(p.states)).length,
        /**
         * Distinct signatures only. An attempt whose blocks supplied none contributes NOTHING, so
         * before the per-block signatures land this reads 0 and a `unique` condition simply never
         * fires. That is deliberate: the two cheap fallbacks are both silently wrong -- treating every
         * attempt as unique is the raw-value-equality failure the design rejected, and treating them
         * all as one makes "unique >= 3" unsatisfiable without saying so. A rule that visibly does
         * nothing is recoverable; one that quietly miscounts is not.
         */
        unique: new Set(picked.map((p) => p.signature).filter((s) => s !== undefined && s !== '')).size,
    };
}
/**
 * Every tally Flow can ask about, for one task, from the log.
 *
 * `pending` is the attempt being resolved RIGHT NOW, before it has an outcome to be recorded with.
 * It counts. "After 3 attempts, offer a hint" has to fire on the third attempt, and a count taken
 * from the log alone would not see it until the fourth -- every attempt condition silently off by
 * one, in the direction that makes a teacher's leniency arrive a try late.
 *
 * The caller cannot simply record first and count after, because the record carries the outcome
 * and the outcome is what this count is for.
 */
export function countsFor(session, taskId, pending) {
    const logged = session.attempts.filter((a) => a.taskId === taskId);
    const mine = pending ? [...logged, pending] : logged;
    /*
     * The pending attempt belongs to the visit they are inside, which is the LAST window. It is
     * appended after the window filter rather than inside it because it has no timestamp yet --
     * it is the submission being resolved, and `at` is stamped when it is recorded.
     */
    const since = lastArrivalAt(session, taskId);
    const thisVisit = since === null
        // A session recorded before arrivals were logged has no window to apply, so every attempt
        // on the task is "this visit". The same forgiving read `visitsOf` gives that data.
        ? mine
        : [...logged.filter((a) => a.at >= since), ...(pending ? [pending] : [])];
    return {
        taskId,
        /*
         * EVERY task's arrivals, not just this one's, because a `visits` condition may ask about
         * somewhere else the student has been. Built here rather than looked up per condition so the
         * engine stays a pure function of what it is handed.
         *
         * No `pending` equivalent: the arrival was logged when the route brought them here, so the
         * visit this submission belongs to is already in the count.
         */
        visits: (session.visits ?? []).reduce((acc, v) => ({ ...acc, [v.taskId]: (acc[v.taskId] ?? 0) + 1 }), {}),
        visit: windowFor(thisVisit),
        session: windowFor(mine),
    };
}
/**
 * When the student last arrived at this task, or `null` if the log cannot say.
 *
 * The LAST arrival, not the first: the visit window is the one they are in. An arrival somewhere
 * else afterwards does not close it for this purpose -- `countsFor` is asked about the task being
 * resolved, and a student resolving a task is on it.
 */
function lastArrivalAt(session, taskId) {
    const mine = (session.visits ?? []).filter((v) => v.taskId === taskId);
    return mine.length > 0 ? mine[mine.length - 1].at : null;
}
/** Tally one stretch of attempts, by task and by block. */
function windowFor(attempts) {
    const blockIds = [...new Set(attempts.flatMap((a) => Object.keys(a.results)))];
    return {
        task: tally(attempts, (a) => ({
            states: Object.values(a.results).flat(),
            /**
             * The task's own signature is every block's, together: two attempts are the same attempt
             * only if nothing about them differed.
             *
             * ALL of them, or none. A signature built from the blocks that happen to have one would
             * call two attempts identical when they differed only in a block that stayed silent --
             * under-counting `unique` without saying so. The same reasoning as the empty case below:
             * a rule that visibly does nothing is recoverable, one that quietly miscounts is not.
             */
            signature: Object.keys(a.results).every((id) => a.signatures[id])
                ? Object.keys(a.signatures).sort().map((k) => `${k}=${a.signatures[k]}`).join('|')
                : undefined,
        })),
        blocks: Object.fromEntries(blockIds.map((id) => [
            id,
            tally(attempts, (a) => (a.results[id] ? { states: a.results[id], signature: a.signatures[id] } : null)),
        ])),
    };
}
/**
 * What the student last handed in on this task, by block.
 *
 * The other half of "walking back into a task does not wipe it": counts and rolls already survive
 * a revisit because they live here, and without this the typed answer did not, so a student sent
 * to a remediation task and back found their work gone.
 *
 * The LAST attempt, not a merge across attempts. Each submission is a complete picture of the
 * task at that moment, and stitching several together would show a student a combination they
 * never submitted -- block A from their third try beside block B from their first.
 */
export function lastAnswersFor(session, taskId, within, 
/**
 * AS OF A MOMENT, for reading the run the way it looked then rather than the way it looks now.
 *
 * Exit text quotes the student back to themselves, and a summary being REVIEWED has to quote
 * the answers as they stood when that exit fired. Without this, flipping back to visit 13
 * shows visit 17's numbers — a sentence describing work the student had not done yet, in the
 * teacher's own words, which reads as the system being wrong rather than early.
 *
 * An ISO timestamp, compared as a string because that is what ISO 8601 is for. Absent means
 * now, which is what everything playing FORWARD wants.
 */
asOf) {
    return lastAttemptFor(session, taskId, within, asOf)?.answers ?? {};
}
/**
 * The last attempt on a task within a window, or none.
 *
 * `lastAnswersFor` is this plus `.answers`, and the two share a definition deliberately: "what
 * did they last hand in" and "what did that submission decide" are questions about the SAME
 * attempt, and answering them from two filters is how they come to disagree.
 *
 * THE VISIT WINDOW IS WHY THIS IS EXPORTED. A task is finished with a student when the attempt
 * THEY JUST MADE routed them away — not when one from a previous visit did. Read over the whole
 * session, a returning student is met by a task that is already over: the bar says Continue, the
 * answers are locked, and the loop they were sent round cannot be re-run.
 */
export function lastAttemptFor(session, taskId, within, asOf) {
    const since = within === 'visit' ? lastArrivalAt(session, taskId) : null;
    const mine = session.attempts.filter((a) => a.taskId === taskId
        && (since === null || a.at >= since)
        && (asOf === undefined || a.at <= asOf));
    return mine[mine.length - 1];
}
/**
 * One entry per VISIT to a task, in order, each the attempt that visit ended on -- which is what
 * review flips through. A visit that answered nothing contributes nothing; there is no attempt
 * to show for it.
 *
 * SEGMENTED BY THE ARRIVAL LOG, which is the whole reason that log exists. This used to group
 * consecutive runs of the same task in the attempt list, and that reading merges two visits
 * whenever nothing was submitted in between: answer here, walk through a page of text, come
 * back, answer again, and the two attempts sit next to each other in the log and read as one
 * visit. Review then showed a student one entry where they had made two passes.
 *
 * A session recorded before arrivals were logged falls back to the old grouping. It is the
 * better answer available for that data rather than a second definition kept alive on purpose --
 * everything written since has a log to segment by.
 */
export function visitsOf(session, taskId) {
    const visits = session.visits ?? [];
    if (visits.length === 0)
        return runsOf(session, taskId);
    // Arrival times bound each window; the last one runs to the end of the session.
    const bounds = visits.map((v) => v.at);
    const ends = [];
    for (let i = 0; i < visits.length; i += 1) {
        if (visits[i].taskId !== taskId)
            continue;
        const from = bounds[i];
        const to = bounds[i + 1];
        const within = session.attempts.filter((a) => a.taskId === taskId && a.at >= from && (to === undefined || a.at < to));
        if (within.length > 0)
            ends.push(within[within.length - 1]);
    }
    return ends;
}
/** The pre-arrival-log reading: a run of consecutive attempts on one task is one visit. */
function runsOf(session, taskId) {
    const ends = [];
    let run = null;
    for (const attempt of session.attempts) {
        if (attempt.taskId === taskId)
            run = attempt;
        else if (run) {
            ends.push(run);
            run = null;
        }
    }
    if (run)
        ends.push(run);
    return ends;
}
//# sourceMappingURL=session.js.map